import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { localizeClassItem } from '../../config/classUiConfig';
import './ProfileOverview.css';

const ProfileOverview = ({ profile, onSectionChange }) => {
    const { t, i18n } = useTranslation('profile');
    const dateLocale = i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';

    const getActiveClassesCount = (enrollments) => {
        if (!enrollments) return 0;
        return enrollments.filter(e =>
            e.enrollment_status === 'approved' &&
            e.enrollment_type === 'active'
        ).length;
    };

    const stats = useMemo(() => [
        {
            label: t('overview.activeClasses'),
            value: getActiveClassesCount(profile?.enrollments),
            icon: 'fa-book',
            section: 'enrollments'
        },
        {
            label: t('overview.certificates'),
            value: profile?.certificates?.length || 0,
            icon: 'fa-certificate',
            section: 'certificates'
        },
        {
            label: t('overview.paymentsDue'),
            value: profile?.payments?.filter(
                (p) => p.status === 'pending' && Number(p.amount) > 0
            ).length || 0,
            icon: 'fa-credit-card',
            section: 'payments'
        },
        {
            label: t('overview.unreadNotifications'),
            value: profile?.notifications?.filter(n => !n.is_read).length || 0,
            icon: 'fa-bell',
            section: 'notifications'
        }
    ], [profile, t]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return t('overview.today');
        if (diffDays === 1) return t('overview.yesterday');
        if (diffDays < 7) return t('overview.daysAgo', { count: diffDays });
        return date.toLocaleDateString(dateLocale);
    };

    const formatActivityText = (activity) => {
        switch (activity.action) {
            case 'profile_update':
                return t('overview.activity.profileUpdate');
            case 'enrollment': {
                const classTitle = activity.details?.class_title;
                const localized = classTitle
                    ? localizeClassItem({ title: classTitle }, i18n.language).title
                    : t('overview.activity.enrolledInFallback');
                return t('overview.activity.enrolledIn', { className: localized });
            }
            case 'payment':
                return t('overview.activity.paymentMade', { amount: activity.details?.amount || '0' });
            case 'certificate':
                return t('overview.activity.certificateEarned');
            default:
                return activity.description || activity.action.replace(/_/g, ' ');
        }
    };

    const recentActivity = profile?.recent_activity?.slice(0, 5) || [];

    return (
        <div className="profile-overview">
            <div className="section-header">
                <h2>{t('overview.title')}</h2>
            </div>

            <div className="stats-grid">
                {stats.map((stat, index) => (
                    <Button
                        key={index}
                        className="stat-card"
                        onClick={() => onSectionChange && onSectionChange(stat.section)}
                        sx={{
                            textTransform: 'none',
                            textAlign: 'left',
                            padding: '1.5rem',
                            width: '100%',
                            justifyContent: 'flex-start'
                        }}
                    >
                        <i className={`fas ${stat.icon}`}></i>
                        <div className="stat-info">
                            <span className="stat-value">{stat.value}</span>
                            <span className="stat-label">{stat.label}</span>
                        </div>
                    </Button>
                ))}
            </div>

            <div className="recent-activity">
                <h3>{t('overview.recentActivity')}</h3>
                {recentActivity.length > 0 ? (
                    <ul className="activity-list">
                        {recentActivity.map((activity, index) => (
                            <li key={index} className="activity-item">
                                <div className="activity-icon">
                                    <i className={`fas ${getActivityIcon(activity.action)}`}></i>
                                </div>
                                <div className="activity-details">
                                    <span className="activity-time">
                                        {formatDate(activity.created_at)}
                                    </span>
                                    <span className="activity-description">
                                        {formatActivityText(activity)}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="empty-state">{t('overview.noActivity')}</p>
                )}
            </div>
        </div>
    );
};

const getActivityIcon = (action) => {
    const icons = {
        profile_update: 'fa-user-edit',
        enrollment: 'fa-book',
        payment: 'fa-credit-card',
        certificate: 'fa-certificate',
        default: 'fa-circle'
    };
    return icons[action] || icons.default;
};

export default ProfileOverview;
